import { Component, OnInit, effect, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { QaiTableComponent, TableRow, HeaderConfig, QaiIconComponent } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { IAssignmentViewStatsResponse } from 'src/app/interfaces/reportingV2/iassignment-view-stats-response';
import { LocaleService } from 'src/app/services/locale.service';
import { CsvExportService, csvValue } from 'src/app/services/general/csv-export.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

@Component({
  selector: 'app-simulation-sessions',
  templateUrl: './simulation-sessions.component.html',
  styleUrls: ['./simulation-sessions.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslocoModule, QaiTableComponent, DetailsSectionComponent, QaiIconComponent],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
})
export class SimulationSessionsComponent implements OnInit {
  readonly totalSessionsCount = input(0);
  readonly sessionStats = input<IAssignmentViewStatsResponse[]>([]);
  readonly accountName = input('');
  readonly simulationName = input('');

  isTranslationLoaded = false;
  isLoading = false;
  currentPage = 1;
  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private localeService: LocaleService,
    private csvExportService: CsvExportService,
    private amplitudeService: AmplitudeService,
  ) {
    effect(() => {
      this.sessionStats();
      if (this.isTranslationLoaded && this.headers.length > 0) {
        this.updateTableData();
      }
    });
  }

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2')
      .then(() => {
        this.isTranslationLoaded = true;
        this.initializeTable();
      })
      .catch((error) => {
        console.error('Error loading translations:', error);
        this.isTranslationLoaded = false;
      });
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate('USER_NAME', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('SESSION_DATE', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2'), 
        sortable: true
      },
      {
        name: this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2'), 
        sortable: true
      }
    ];

    this.updateTableData();
  }

  private updateTableData(): void {
    const stats = this.sessionStats();
    if (stats.length > 0) {
      this.tableData = this.transformSessionStatsToTableRows(stats);
    } else {
      this.tableData = [];
    }
  }

  private transformSessionStatsToTableRows(stats: IAssignmentViewStatsResponse[]): TableRow[] {
    return stats.map(stat => {
      const safeValue = (val: any, formatter?: (v: any) => string): string => {
        if (val === null || val === undefined) {
          return '—';
        }
        return formatter ? formatter(val) : val.toString();
      };

      const row: any = {
        id: stat.uuid,
        [this.translocoService.translate('USER_NAME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(stat.userName)
        },
        [this.translocoService.translate('SESSION_DATE', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(stat.sessionDate, v => this.localeService.formatSessionDateTime(v)),
          ...csvValue(this.localeService.formatCsvDateTime(stat.sessionDate)),
          ...(stat.sessionDate ? { sortValue: stat.sessionDate } : {})
        },
        [this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(stat.durationSeconds, v => this.localeService.formatDurationInSecondsWithLabel(v, true))
        },
      [this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.overallScore, v => this.localeService.formatScoreValue(v))
      },
      [this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.messagingScore, v => this.localeService.formatScoreValue(v))
      },
      [this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.methodologyScore, v => this.localeService.formatScoreValue(v))
      },
      [this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2')]: {
        type: 'text',
        value: safeValue(stat.skillsScore, v => this.localeService.formatScoreValue(v))
      }
      };
      return row as TableRow;
    });
  }

  onPageChanged(event: any): void {
    this.currentPage = event.page;
  }

  onDownloadClick(): void {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTING_SESSIONS_CSV_EXPORTED);

    const fileName = this.csvExportService.getSimulationSessionsCsvFileName(
      this.accountName(),
      this.simulationName()
    );
    const sessionIdHeader = this.translocoService.translate('SESSION_ID', {}, 'reportingV2');

    const csvData = this.tableData.map(row => {
      const csvRow: Record<string, unknown> = {
        [sessionIdHeader]: row.id,
      };

      this.headers.forEach(header => {
        csvRow[header.name] = row[header.name];
      });

      return csvRow;
    });

    const headerNames = [sessionIdHeader, ...this.headers.map(h => h.name)];

    this.csvExportService.exportToCsv(csvData, fileName, headerNames);
  }
}

